import type { Pinia } from 'pinia';
import { authService } from '~/services/auth-service';
import type { AuthUser } from '~/types/auth/auth';

/**
 * Saved state outlives the session that wrote it. A sign-out on the auth host or in another app never
 * reaches this tab's sessionStorage, so the next member to sign in and come back to checkout in this
 * tab opens a page that hydrates the identity the last one saved, which the page shows, prefills and
 * charges for. It is kept only while it names the member the session's access token names (the auth
 * store's `sessionMember`). Any other is dropped, with its saved entry, before the page reads it, and
 * the page's account load (loadMe) loads the member the session names.
 */
function forgetAnotherMembersPerson(pinia: Pinia): void {
    const person = usePersonStore(pinia);
    if (person.person && person.person.identity?.id !== useAuthStore(pinia).sessionMember) {
        person.clear();
        person.$persist();
    }
}

export const usePersonStore = defineStore(
    'person',
    () => {
        const person = ref<AuthUser | null>(null);
        const loaded = ref(false);

        async function loadMe() {
            const user = await authService.getMe();
            person.value = user;
            loaded.value = true;
            return user;
        }

        function clear() {
            person.value = null;
            loaded.value = false;
        }

        return { person, loaded, loadMe, clear };
    },
    {
        persist: {
            pick: ['person'],
            afterHydrate: ({ pinia }) => forgetAnotherMembersPerson(pinia),
        },
    }
);

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(usePersonStore, import.meta.hot));
}
